import * as Sentry from "@sentry/react";
import reportErrorSentry from "src/lib/reportErrorSentry";
import { logSentryEvent } from "src/lib/logSentryEvent";

/**
 * Auth flows that can fail. Used as the Sentry `flow` tag AND the
 * `auth.<flow>.failure` structured-log name. snake_case to match the existing
 * event names (`auth.login.*`, `auth.token_refresh.*`).
 */
export type AuthFlow =
  | "login"
  | "logout"
  | "register"
  | "anonymous"
  | "token_refresh"
  | "token_check"
  | "password_reset"
  | "saml"
  | "ott"
  | "handover"
  | "credential_store"
  | "auth_stage";

/**
 * Report an auth-flow failure with consistent, insightful context.
 * Emits an `auth.<flow>.failure` structured log (aggregatable, fires in all
 * envs) AND forwards the exception to reportErrorSentry (captured as a Sentry
 * issue in prod, still subject to central 4xx / 9am-code filtering).
 * reportErrorSentry already attaches hasAuthToken / hasRefreshToken /
 * authStatus / HTTP-status / traceId; `context` adds flow-specific detail
 * (step, partner, userId, tokenLength, …). Never pass raw tokens/passwords.
 */
export function reportAuthError(
  flow: AuthFlow,
  error: unknown,
  context: Record<string, unknown> = {},
  options?: { expected?: boolean; level?: Sentry.SeverityLevel }
): void {
  logSentryEvent(`auth.${flow}.failure`, { flow, ...context }, "error");
  reportErrorSentry(error, { flow, ...context }, options);
}
